/**
 * Keeps a saved timesheet readable after its project stops being selectable.
 *
 * Timesheet rows store the project and phase as names, and the dropdowns are
 * built from the user's *currently assigned, active* projects
 * (getAssignedProjectsById filters on status: 'active'). So the moment a
 * project is completed, canceled or unassigned, every historical row that
 * referenced it renders as an empty dropdown — the value is still there, but
 * no option matches it, and a MUI Select with an unmatched value shows blank.
 *
 * That loses real data on screen: hours that were logged against a project
 * that was perfectly active at the time.
 *
 * This merges anything a row actually references back into the options, so a
 * saved week always displays what it says. Merged-in entries are flagged
 * `unavailable` so the UI can mark them as closed to new entry — they are for
 * reading history, not for starting new work against.
 */
export function withHistoricalOptions(projects = [], rows = []) {
  const options = (projects || []).map((project) => ({
    ...project,
    phases: project.phases || [],
  }));
  const byName = new Map(options.map((project) => [project.projectName, project]));

  (rows || []).forEach((row) => {
    const projectName = (row?.projectName || "").trim();
    if (!projectName) return;

    let project = byName.get(projectName);
    if (!project) {
      project = { projectName, phases: [], unavailable: true };
      byName.set(projectName, project);
      options.push(project);
    }

    // The phase can disappear on its own, even while the project is still
    // active — a phase deleted after the time was logged against it.
    const phaseName = (row?.phaseName || "").trim();
    if (!phaseName) return;
    if (!project.phases.some((phase) => phase.name === phaseName)) {
      project.phases = [
        ...project.phases,
        { id: `historical:${projectName}:${phaseName}`, name: phaseName, unavailable: true },
      ];
    }
  });

  return options;
}

/** Appends the "no longer available" note to a merged-in option's label. */
export const optionLabel = (name, unavailable) =>
  unavailable ? `${name} (no longer available)` : name;

export default withHistoricalOptions;
